// ============================================================
//  src/admin/api/adminClient.js
//  Separate axios instance for /bot-father admin endpoints.
//  Uses its own token (aAuthToken) — completely independent
//  from the subscriber-facing API client/session.
// ============================================================
import axios from 'axios';

const ADMIN_API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
});

ADMIN_API.interceptors.request.use(cfg => {
  const token = localStorage.getItem('aAuthToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

ADMIN_API.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('aAuthToken');
      localStorage.removeItem('aAdmin');
      if (!window.location.pathname.endsWith('/bot-father/login')) {
        window.location.href = '/bot-father/login';
      }
    }
    return Promise.reject(err);
  }
);

// ── Auth ─────────────────────────────────────────────────────
export const adminLogin  = (email, password) => ADMIN_API.post('/bot-father/login/', { email, password });
export const adminLogout = () => ADMIN_API.post('/bot-father/session/');
export const adminMe     = () => ADMIN_API.get('/bot-father/session/');

// ── Dashboard ────────────────────────────────────────────────
export const getDashboard = () => ADMIN_API.get('/bot-father/dashboard/');

// ── Users ────────────────────────────────────────────────────
export const listUsers   = (params) => ADMIN_API.get('/bot-father/users/', { params });
export const getUser     = (sId)    => ADMIN_API.get('/bot-father/users/detail/', { params: { sId } });
export const userAction  = (data)   => ADMIN_API.post('/bot-father/users/action/', data);

// ── Bots ─────────────────────────────────────────────────────
export const listBots  = (params) => ADMIN_API.get('/bot-father/bots/', { params });
export const getBot    = (bId)    => ADMIN_API.get('/bot-father/bots/detail/', { params: { bId } });
export const botAction = (data)   => ADMIN_API.post('/bot-father/bots/action/', data);

export const getQueueSettings  = () => ADMIN_API.get('/bot-father/queue/');
export const saveQueueSettings = (enabled) => ADMIN_API.post('/bot-father/queue/', { enabled });

// ── Plans ────────────────────────────────────────────────────
export const adminListPlans  = () => ADMIN_API.get('/bot-father/plans/');
export const adminCreatePlan = (data) => ADMIN_API.post('/bot-father/plans/', { action: 'create', ...data });
export const adminUpdatePlan = (data) => ADMIN_API.post('/bot-father/plans/', { action: 'update', ...data });
export const adminDeletePlan = (plId) => ADMIN_API.post('/bot-father/plans/', { action: 'delete', plId });

// ── PocketFi config ──────────────────────────────────────────
export const getPocketfiConfig  = () => ADMIN_API.get('/bot-father/pocketfi/');
export const savePocketfiConfig = (data) => ADMIN_API.post('/bot-father/pocketfi/', data);

// ── Rent Space (admin) ───────────────────────────────────────
export const getRentSpaceConfig  = () => ADMIN_API.get('/bot-father/rent-space/');
export const saveRentSpaceConfig = data => ADMIN_API.post('/bot-father/rent-space/', { action: 'save_config', ...data });
export const listRentSpaceRentals = () => ADMIN_API.get('/bot-father/rent-space/', { params: { action: 'list' } });
export const pauseRental  = rnId => ADMIN_API.post('/bot-father/rent-space/', { action: 'pause', rnId });
export const resumeRental = rnId => ADMIN_API.post('/bot-father/rent-space/', { action: 'resume', rnId });
// Meta's API does not support true deletion of a phone number (confirmed
// directly against Meta's own docs) -- only deregistration, which stops
// the number sending/receiving but does NOT remove it from the WABA's
// number count or delete its history. True deletion requires a business
// portfolio admin acting manually in WhatsApp Manager.
export const deregisterRental = (rnId, confirmPhoneNumber) =>
  ADMIN_API.post('/bot-father/rent-space/', { action: 'deregister', rnId, confirm: true, confirmPhoneNumber });

// Checks Meta's live state for this number and removes the local record
// if Meta confirms it no longer exists (e.g. deleted manually in
// WhatsApp Manager, which our database has no automatic way of knowing).
export const verifyRentalStatus = rnId =>
  ADMIN_API.post('/bot-father/rent-space/', { action: 'verify_status', rnId });

// Preview (confirm omitted/false) or actually remove (confirm: true) local
// Flow rows that leaked across renters before "Sync from Meta" was blocked
// for rented numbers -- see RentSpaceController::adminCleanupLeakedFlows.
export const previewLeakedFlows = () =>
  ADMIN_API.post('/bot-father/rent-space/', { action: 'cleanup_leaked_flows' });
export const cleanupLeakedFlows = () =>
  ADMIN_API.post('/bot-father/rent-space/', { action: 'cleanup_leaked_flows', confirm: true });

// Pushes the current platform token/WABA ID/App Secret/Meta App ID onto
// every bot already linked to a rental -- covers rental bots created
// before Bot::createBot() was fixed to actually persist those fields.
export const syncPlatformIdsToBots = () =>
  ADMIN_API.post('/bot-father/rent-space/', { action: 'sync_platform_ids_to_bots' });

// Manual per-bot flow review: list a rental bot's local flow rows, and
// remove specific ones the admin/customer has confirmed aren't theirs.
export const listBotFlows = bId =>
  ADMIN_API.post('/bot-father/rent-space/', { action: 'list_bot_flows', bId });
export const removeBotFlows = (bId, fIds) =>
  ADMIN_API.post('/bot-father/rent-space/', { action: 'remove_bot_flows', bId, fIds });

// ── Payments ─────────────────────────────────────────────────
export const listPayments = (params) => ADMIN_API.get('/bot-father/payments/', { params });

// ── Logs ─────────────────────────────────────────────────────
export const listLogs = (params) => ADMIN_API.get('/bot-father/logs/', { params });

// ── Admin & role management (superadmin only) ───────────────
export const listAdmins       = () => ADMIN_API.get('/bot-father/admins/');
export const createAdmin      = (data) => ADMIN_API.post('/bot-father/admins/action/', { action: 'create', ...data });
export const setAdminActive   = (aId, active) => ADMIN_API.post('/bot-father/admins/action/', { action: active ? 'activate' : 'deactivate', aId });
export const setAdminRole     = (aId, aRole) => ADMIN_API.post('/bot-father/admins/action/', { action: 'change_role', aId, aRole });
export const resetAdminPassword = (aId) => ADMIN_API.post('/bot-father/admins/action/', { action: 'reset_password', aId });

// ── Broadcasts (platform-wide monitoring) ────────────────────
export const listBroadcasts = (params) => ADMIN_API.get('/bot-father/broadcasts/', { params });

export default ADMIN_API;